/**
 * StudioTransport — the bar between the patch bar and the matrix.
 *
 * Play/stop, the moving position readout, tempo, swing, step count, pattern
 * seeding/clearing, the VIEW collapse control, and master level with meters.
 * It renders and reports; it does not own timing or audio.
 */

const STEP_COUNTS = [8, 16, 32];

const VIEW_MODES = [
  { id: 'both', label: 'Parameters and sequencer', glyphs: ['', ''] },
  { id: 'params', label: 'Parameters, sequencer as mini-map', glyphs: ['is-wide', 'is-thin'] },
  { id: 'steps', label: 'Sequencer only', glyphs: ['is-thin', 'is-wide'] },
];

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function rule() {
  return el('div', 'pm-rule');
}

/** Micro-caps label stacked over a value — the transport's only text form. */
function field(labelText, valueNode, id) {
  const wrap = el('div', 'pm-field');
  wrap.appendChild(el('div', 'pm-field-label', labelText));
  valueNode.classList.add('pm-field-value');
  if (id) valueNode.id = id;
  wrap.appendChild(valueNode);
  return wrap;
}

export class StudioTransport {
  constructor(options = {}) {
    this.bpm = options.bpm ?? 120;
    this.swing = options.swing ?? 0;
    this.stepCount = options.stepCount ?? 16;
    this.view = options.view || 'both';
    this.master = options.master ?? 0.8;
    this.isPlaying = false;

    this.onPlay = options.onPlay || (() => {});
    this.onStop = options.onStop || (() => {});
    this.onBpmChange = options.onBpmChange || (() => {});
    this.onSwingChange = options.onSwingChange || (() => {});
    this.onStepCountChange = options.onStepCountChange || (() => {});
    this.onSeedPattern = options.onSeedPattern || (() => {});
    this.onClearPattern = options.onClearPattern || (() => {});
    this.onViewChange = options.onViewChange || (() => {});
    this.onMasterChange = options.onMasterChange || (() => {});

    this.element = this.render();
    this.setBpm(this.bpm);
    this.setSwing(this.swing);
    this.setStepCount(this.stepCount);
    this.setView(this.view);
    this.setMaster(this.master);
    this.setPosition(-1);
  }

  render() {
    const bar = el('div', 'transport-zone');
    bar.setAttribute('role', 'toolbar');
    bar.setAttribute('aria-label', 'Transport');

    /* ---- play / stop ---- */

    const buttons = el('div', 'pm-transport-btns');

    this.playBtn = el('button', 'pm-icon-btn is-play', '▶');
    this.playBtn.type = 'button';
    this.playBtn.id = 'play-btn';
    this.playBtn.title = 'Play (Space)';
    this.playBtn.setAttribute('aria-label', 'Play');
    this.playBtn.setAttribute('aria-pressed', 'false');
    this.playBtn.addEventListener('click', () => this.onPlay());

    this.stopBtn = el('button', 'pm-icon-btn', '■');
    this.stopBtn.type = 'button';
    this.stopBtn.id = 'stop-btn';
    this.stopBtn.title = 'Stop (Space)';
    this.stopBtn.setAttribute('aria-label', 'Stop');
    this.stopBtn.addEventListener('click', () => this.onStop());

    buttons.append(this.playBtn, this.stopBtn);
    bar.appendChild(buttons);
    bar.appendChild(rule());

    /* ---- position ---- */

    this.positionValue = el('div', null, '–.–.–');
    bar.appendChild(field('POSITION', this.positionValue, 'position-readout'));
    bar.appendChild(rule());

    /* ---- tempo ---- */

    this.bpmValue = el('div');
    this.bpmDigits = el('span', null, '120');
    const stepper = el('div', 'pm-stepper');
    const up = el('button', null, '▲');
    up.type = 'button';
    up.setAttribute('aria-label', 'Increase tempo');
    up.addEventListener('click', () => this.nudgeBpm(1));
    const down = el('button', null, '▼');
    down.type = 'button';
    down.setAttribute('aria-label', 'Decrease tempo');
    down.addEventListener('click', () => this.nudgeBpm(-1));
    stepper.append(up, down);
    this.bpmValue.append(this.bpmDigits, stepper);

    const bpmField = field('BPM', this.bpmValue, 'bpm-readout');
    this.bindNumberDrag(this.bpmDigits, {
      label: 'Tempo',
      min: 40,
      max: 240,
      pixelsPerUnit: 3,
      get: () => this.bpm,
      set: (v) => this.applyBpm(v),
    });
    bar.appendChild(bpmField);
    bar.appendChild(rule());

    /* ---- swing ---- */

    this.swingValue = el('div');
    this.swingDigits = el('span', null, '0');
    const percent = el('small', null, '%');
    this.swingValue.append(this.swingDigits, percent);
    bar.appendChild(field('SWING', this.swingValue, 'swing-readout'));
    this.bindNumberDrag(this.swingDigits, {
      label: 'Swing',
      min: 0,
      max: 75,
      pixelsPerUnit: 2,
      get: () => this.swing,
      set: (v) => this.applySwing(v),
    });
    bar.appendChild(rule());

    /* ---- step count ---- */

    const stepsWrap = el('div', 'pm-field');
    stepsWrap.appendChild(el('div', 'pm-field-label', 'STEPS'));
    this.stepSeg = el('div', 'pm-seg step-count-control');
    this.stepButtons = STEP_COUNTS.map((count) => {
      const btn = el('button', 'pm-seg-btn', String(count));
      btn.type = 'button';
      btn.dataset.steps = String(count);
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', `${count} steps`);
      btn.addEventListener('click', () => this.onStepCountChange(count));
      this.stepSeg.appendChild(btn);
      return btn;
    });
    stepsWrap.appendChild(this.stepSeg);
    bar.appendChild(stepsWrap);
    bar.appendChild(rule());

    /* ---- pattern actions ---- */

    const seed = el('button', 'pm-icon-btn pm-dice');
    seed.type = 'button';
    seed.id = 'seed-pattern-btn';
    seed.title = 'Seed a musical pattern';
    seed.setAttribute('aria-label', 'Seed a musical pattern');
    // Five pips on a 3x3 — the die face, drawn rather than typed so it keeps
    // its weight next to the other controls.
    [1, 0, 1, 0, 1, 0, 1, 0, 1].forEach((on) => {
      const pip = el('span');
      if (!on) pip.classList.add('is-off');
      seed.appendChild(pip);
    });
    seed.addEventListener('click', () => this.onSeedPattern());

    const clear = el('button', 'action-btn clear-btn', 'CLEAR PATTERN');
    clear.type = 'button';
    clear.id = 'clear-pattern-btn';
    clear.addEventListener('click', () => this.onClearPattern());

    const patternWrap = el('div', 'pm-transport-btns');
    patternWrap.style.gap = '9px';
    patternWrap.append(seed, clear);
    bar.appendChild(patternWrap);
    bar.appendChild(rule());

    /* ---- view ---- */

    const viewWrap = el('div', 'pm-field');
    viewWrap.appendChild(el('div', 'pm-field-label', 'VIEW'));
    this.viewSeg = el('div', 'pm-seg view-control');
    this.viewButtons = VIEW_MODES.map((mode) => {
      const btn = el('button', 'pm-seg-btn pm-view-btn');
      btn.type = 'button';
      btn.dataset.view = mode.id;
      btn.title = mode.label;
      btn.setAttribute('aria-label', mode.label);
      btn.setAttribute('aria-pressed', 'false');
      mode.glyphs.forEach((glyph) => {
        const pane = el('i', glyph || null);
        btn.appendChild(pane);
      });
      btn.addEventListener('click', () => this.onViewChange(mode.id));
      this.viewSeg.appendChild(btn);
      return btn;
    });
    viewWrap.appendChild(this.viewSeg);
    bar.appendChild(viewWrap);

    bar.appendChild(el('div', 'pm-spacer'));

    /* ---- master ---- */

    const master = el('div', 'pm-master');
    master.appendChild(el('div', 'pm-field-label', 'MASTER'));

    this.masterTrack = el('div', 'pm-master-fader');
    this.masterTrack.setAttribute('role', 'slider');
    this.masterTrack.setAttribute('tabindex', '0');
    this.masterTrack.setAttribute('aria-label', 'Master level');
    this.masterTrack.setAttribute('aria-valuemin', '0');
    this.masterTrack.setAttribute('aria-valuemax', '100');
    this.masterFill = el('i');
    this.masterThumb = el('b');
    this.masterTrack.append(this.masterFill, this.masterThumb);
    this.bindMasterFader();

    this.masterDb = el('div', 'pm-master-db', '-1.9dB');

    const meters = el('div', 'pm-master-meters');
    this.masterMeters = [0, 1].map(() => {
      const lane = el('div');
      const fill = el('i');
      lane.appendChild(fill);
      meters.appendChild(lane);
      return fill;
    });

    master.append(this.masterTrack, this.masterDb, meters);
    bar.appendChild(master);

    return bar;
  }

  /* ---------------------------------------------------------------- *
   * Interaction
   * ---------------------------------------------------------------- */

  /**
   * Turn a readout into a draggable number with keyboard parity. Tempo and
   * swing are both "grab and pull" values in hardware, and the arrow keys
   * keep them reachable without a pointer.
   */
  bindNumberDrag(node, { label, min, max, pixelsPerUnit, get, set }) {
    node.setAttribute('role', 'spinbutton');
    node.setAttribute('tabindex', '0');
    node.setAttribute('aria-label', label);
    node.setAttribute('aria-valuemin', String(min));
    node.setAttribute('aria-valuemax', String(max));
    node.style.cursor = 'ns-resize';
    node.style.touchAction = 'none';

    let dragging = false;
    let startY = 0;
    let startValue = 0;

    node.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      dragging = true;
      startY = e.clientY;
      startValue = get();
      node.setPointerCapture(e.pointerId);
      e.preventDefault();
    });

    node.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const delta = Math.round((startY - e.clientY) / pixelsPerUnit);
      set(Math.min(max, Math.max(min, startValue + delta)));
    });

    const end = (e) => {
      if (!dragging) return;
      dragging = false;
      if (node.hasPointerCapture?.(e.pointerId)) node.releasePointerCapture(e.pointerId);
    };
    node.addEventListener('pointerup', end);
    node.addEventListener('pointercancel', end);

    node.addEventListener('keydown', (e) => {
      let direction = 0;
      if (e.key === 'ArrowUp' || e.key === 'ArrowRight') direction = 1;
      else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') direction = -1;
      else return;
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      set(Math.min(max, Math.max(min, get() + direction * step)));
    });
  }

  bindMasterFader() {
    let dragging = false;

    const apply = (clientX) => {
      const rect = this.masterTrack.getBoundingClientRect();
      if (!rect.width) return;
      this.onMasterChange(Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)));
    };

    this.masterTrack.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      dragging = true;
      this.masterTrack.setPointerCapture(e.pointerId);
      apply(e.clientX);
      e.preventDefault();
    });
    this.masterTrack.addEventListener('pointermove', (e) => {
      if (dragging) apply(e.clientX);
    });
    const end = (e) => {
      if (!dragging) return;
      dragging = false;
      if (this.masterTrack.hasPointerCapture?.(e.pointerId)) {
        this.masterTrack.releasePointerCapture(e.pointerId);
      }
    };
    this.masterTrack.addEventListener('pointerup', end);
    this.masterTrack.addEventListener('pointercancel', end);

    this.masterTrack.addEventListener('keydown', (e) => {
      let direction = 0;
      if (e.key === 'ArrowUp' || e.key === 'ArrowRight') direction = 1;
      else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') direction = -1;
      else return;
      e.preventDefault();
      this.onMasterChange(this.master + direction * (e.shiftKey ? 0.1 : 0.02));
    });
  }

  nudgeBpm(direction) {
    this.applyBpm(Math.min(240, Math.max(40, this.bpm + direction)));
  }

  applyBpm(value) {
    const next = Math.round(value);
    if (next === this.bpm) return;
    this.onBpmChange(next);
  }

  applySwing(value) {
    const next = Math.round(value);
    if (next === this.swing) return;
    this.onSwingChange(next);
  }

  /* ---------------------------------------------------------------- *
   * State in
   * ---------------------------------------------------------------- */

  setPlaying(isPlaying) {
    this.isPlaying = isPlaying;
    this.playBtn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    this.playBtn.textContent = isPlaying ? '❚❚' : '▶';
    this.playBtn.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
    if (!isPlaying) this.setPosition(-1);
  }

  setBpm(bpm) {
    this.bpm = bpm;
    this.bpmDigits.textContent = String(bpm);
    this.bpmDigits.setAttribute('aria-valuenow', String(bpm));
  }

  setSwing(swing) {
    this.swing = swing;
    this.swingDigits.textContent = String(swing);
    this.swingDigits.setAttribute('aria-valuenow', String(swing));
  }

  setStepCount(count) {
    this.stepCount = count;
    this.stepButtons.forEach((btn) => {
      btn.setAttribute('aria-pressed', Number(btn.dataset.steps) === count ? 'true' : 'false');
    });
  }

  setView(view) {
    this.view = view;
    this.viewButtons.forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.view === view ? 'true' : 'false');
    });
  }

  setMaster(value) {
    this.master = Math.min(1, Math.max(0, value));
    const percent = `${this.master * 100}%`;
    this.masterFill.style.width = percent;
    this.masterThumb.style.left = percent;
    this.masterDb.textContent = `${this.formatDb(this.master)}dB`;
    this.masterTrack.setAttribute('aria-valuenow', String(Math.round(this.master * 100)));
    this.masterTrack.setAttribute('aria-valuetext', `${this.formatDb(this.master)} dB`);
  }

  formatDb(gain) {
    if (gain <= 0.001) return '-∞';
    const db = 20 * Math.log10(gain);
    return db >= 0 ? `+${db.toFixed(1)}` : db.toFixed(1);
  }

  /** Step index → `bar.beat.sixteenth`, or dashes when stopped. */
  setPosition(stepIndex) {
    if (stepIndex < 0) {
      this.positionValue.textContent = '–.–.–';
      return;
    }
    const bar = Math.floor(stepIndex / 16) + 1;
    const beat = Math.floor((stepIndex % 16) / 4) + 1;
    const sub = (stepIndex % 4) + 1;
    this.positionValue.textContent = `${bar}.${beat}.${sub}`;
  }

  /** Two master meter bars, 0–1 each. */
  setMeters(left, right) {
    this.masterMeters[0].style.width = `${Math.min(1, Math.max(0, left)) * 100}%`;
    this.masterMeters[1].style.width = `${Math.min(1, Math.max(0, right)) * 100}%`;
  }

  getElement() {
    return this.element;
  }

  destroy() {
    this.element.remove();
  }
}
